import React from 'react';
import { useTranslation } from 'next-i18next';

import { CartSlot } from 'components/cart';
import { PageSeo } from 'components/common';
import { Layout } from 'components/layout';
import { Error, Skeleton } from 'components/ui';
import { APP_ROUTES } from 'constants/routes';
import { TRANSLATIONS } from 'constants/translations';
import { getPromotionPageSeoProps } from 'helpers/SeoHelpers';
import { createStaticPaths, prefetchConfig } from 'helpers/ServerHelpers';
import { withSSGPage } from 'hoc/WithServerPage/withSSGPage';
import { prefetchPromotion, usePromotion } from 'hooks/data/usePromotion';
import { prefetchPromotions, usePromotions } from 'hooks/data/usePromotions';
import PromotionTemplate from 'templates/Promotions/PromotionTemplate';

import styles from 'templates/Promotions/PromotionTemplate.module.scss';

import { type NextPageWithLayout, PageType } from 'types';

export const getStaticPaths = createStaticPaths();

export const getStaticProps = withSSGPage(async (context, props) => {

  const { urlParams, queryClient } = props;

  const [isPromotionFound] = await Promise.all([
    prefetchPromotion(queryClient, urlParams),
    prefetchPromotions(queryClient),
    prefetchConfig(queryClient)
  ]);

  // Redirect to root if not found
  if (!isPromotionFound) {
    return {
      redirect: {
        permanent: false,
        destination: `${APP_ROUTES.PROMOTIONS}`
      },
      revalidate: 10
    };
  }
  return {
    props: {}
  };

}, {
  translations: [TRANSLATIONS.COMMON],
  pageType: PageType.Promotion,
  listingMode: 'default',
  revalidate: 10
});

const Page: NextPageWithLayout<PageType.Promotion> = (props) => {
  const {
    urlParams
  } = props;

  const { t } = useTranslation();

  const {
    isLoading, isError, isSuccess, data: promotion
  } = usePromotion(urlParams);

  const {
    isLoading: isPromotionsLoading,
    isError: isPromotionsError,
    isSuccess: isPromotionsSuccess,
    data: promotions,
  } = usePromotions();

  const pageSeoProps = getPromotionPageSeoProps(t, urlParams, promotion?.name);

  // TODO handle error, loading and success inside PromotionTemplate
  return (
    <>
      <PageSeo {...pageSeoProps} />
      {(isError || isPromotionsError) && <Error />}
      {(isLoading || isPromotionsLoading) && <Skeleton count={3} />}
      {(isSuccess && isPromotionsSuccess) && (
        <PromotionTemplate
          urlParams={urlParams}
          promotion={promotion}
          promotions={promotions}
        />
      )}
    </>
  );
};

const layoutProps = {
  containerClassName: styles.layoutContainer,
  rightSlot: {
    component: CartSlot,
    props: {}
  },
  header: {
    props: {
      hideCartOnLargeScreens: true,
    },
    isVisible: true
  }
};

Page.getLayout = (page) => (
  <Layout {...layoutProps}>
    {page}
  </Layout>
);

export default Page;
